/**
 * TOOL LAB — CodeAI101 chapter 3, one reply and everything under it.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next" → chapter 3, then "finish ch3".
 *
 * ── THE CHAT PANE STAYS EMPTY, AND THAT IS THE DESIGN ─────────────────────────────────────────
 * §3: "You see one response from the AI in your chat. Underneath, two tool calls happened, each one
 * a real shell command on a real machine." The product is BUILT to hide that, so a lab that showed
 * the steps alone would only be a diagram. Both panes are on screen from the first click: the chat
 * sits blank through six steps while shell commands go past beside it, and only then does one tidy
 * sentence appear. The blankness is the argument.
 *
 * ── SUPERVISION GATES, IT DOES NOT IMPROVE ────────────────────────────────────────────────────
 * Switching to approve-before stops the dance at the proposal — it does not make the AI propose
 * anything better. §5: "The AI proposes the tool call; the human reviews and explicitly approves
 * before the harness runs it." A lab where a stricter tier quietly produced a wiser call would be
 * teaching that approval fixes judgement, which is the opposite of why approval exists.
 *
 * ── NOTHING RUNS. NOTHING CAN. ────────────────────────────────────────────────────────────────
 * The commands on screen are real ones — df -h, du -sh, and in the failure modes delete_file and
 * send_email. This page executes nothing and imports nothing that could. A lab about the danger of
 * handing an AI a shell, which handed a browser a shell, is the joke that writes itself.
 */

import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import Anchor from './anchor.jsx';
import {
  STEPS, TIERS, FAILURES, danceTo, THE_ASK, CHAT_REPLY,
  THE_GAP, IN_A_LOOP, THE_SHIFT, CLAUDE_CODE_NOTE, SOURCE_3, SOURCE_4, SOURCE_5,
} from './tooldance.mjs';

export default function ToolLab() {
  const [step, setStep] = useState(0);
  const [tier, setTier] = useState('auto');
  const [failure, setFailure] = useState(null);
  const [approved, setApproved] = useState(false);

  const d = danceTo(step, { tier: approved ? 'auto' : tier, failure });
  const restart = (patch = {}) => { setStep(0); setApproved(false); Object.entries(patch).forEach(([k, v]) => (k === 'tier' ? setTier(v) : setFailure(v))); };

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Tool lab · the dance</div>
      <h2 className="g-h2 lab-h">One reply in the chat. Two shell commands underneath.</h2>
      <Anchor chapter={3} />
      <p className="g-body">
        You ask: <b>“{THE_ASK}”</b>. What you will see is a sentence. What happens is on the right.
      </p>

      {/* ── CONDITIONS ── */}
      <div className="lab-task-head">
        <span className="g-label">Supervision</span>
        {TIERS.map((t) => (
          <button key={t.id} className={`g-btn ${tier === t.id ? 'g-btn-primary' : ''}`}
            onClick={() => restart({ tier: t.id })}>{t.name.split(' (')[0]}</button>
        ))}
      </div>
      <p className="g-body g-dim tol-tier">
        <Value of={cited(TIERS.find((t) => t.id === tier).quote, SOURCE_5)} />
      </p>

      <div className="lab-task-head">
        <span className="g-label">Inject a failure</span>
        <button className={`g-btn ${!failure ? 'g-btn-primary' : ''}`} onClick={() => restart({ failure: null })}>none</button>
        {FAILURES.map((f) => (
          <button key={f.id} className={`g-btn ${failure === f.id ? 'g-btn-primary' : ''}`}
            onClick={() => restart({ failure: f.id })}>{f.name}</button>
        ))}
      </div>

      <div className="lab-task-head">
        <button className="g-btn g-btn-primary" disabled={step >= 7 || Boolean(d.gatedAt && step >= d.gatedAt)}
          onClick={() => setStep((s) => Math.min(7, s + 1))}>Step</button>
        <button className="g-btn" disabled={Boolean(d.gatedAt)} onClick={() => setStep(7)}>Run it all</button>
        <button className="g-btn" onClick={() => restart()}>Reset</button>
      </div>

      <div className="tol-two">
        {/* ── WHAT YOU SEE ── */}
        <div className="tol-pane">
          <div className="g-label">What you see in the chat</div>
          <div className="tol-chat">
            <div className="tol-you">{THE_ASK}</div>
            {d.chatVisible
              ? <div className="tol-ai">{CHAT_REPLY}</div>
              : <div className="tol-waiting">{step === 0 ? '—' : 'thinking…'}</div>}
          </div>
          <p className="g-body g-dim tol-note">
            {d.chatVisible
              ? 'One sentence. Nothing about it says that two commands ran on a real machine.'
              : 'Nothing yet. Everything happening is happening out of sight.'}
          </p>
        </div>

        {/* ── WHAT ACTUALLY HAPPENS ── */}
        <div className="tol-pane">
          <div className="g-label">What actually happens</div>
          {d.steps.map((s) => {
            const done = s.n <= d.reached;
            const gated = d.gatedAt === s.n && step >= s.n;
            return (
              <div key={s.n} className={`tol-step ${done ? 'tol-done' : ''} ${gated ? 'tol-gated' : ''} tol-${s.actor}`}>
                <div className="tol-shead">
                  <span className="cou-n">{s.n}</span>
                  <span className={`ctx-voice ctx-${s.actor === 'ai' ? 'assistant' : 'system'}`}>{s.actor}</span>
                  {s.call && <code className="tol-call">{s.call.tool}({s.call.param})</code>}
                  {s.runs && <code className="tol-runs">$ {s.runs}</code>}
                </div>
                {done && <p className="g-body tol-text">{s.text}</p>}
                {gated && (
                  <div className="tol-gate">
                    <p className="g-body"><b>Waiting for you.</b> The AI has proposed this call. Nothing has run.</p>
                    <button className="g-btn g-btn-primary" onClick={() => setApproved(true)}>Approve it</button>
                  </div>
                )}
              </div>
            );
          })}
          <div className="lab-readout">
            <div><span className="g-label">Tool calls proposed</span><Value of={computed(String(d.toolCalls))} /></div>
            <div><span className="g-label">Commands actually run</span><Value of={computed(String(d.ranCommands))} /></div>
          </div>
        </div>
      </div>

      {/* ── WHAT THE FAILURE DID ── */}
      {d.failure && (
        <div className="lab-task">
          <div className="g-label">{d.failure.name}</div>
          <p className="g-body"><Value of={cited(d.failure.quote, SOURCE_4)} /></p>
          {d.actionTaken
            ? <p className="g-body tol-acted">
                <b>The harness ran it.</b> Not a wrong sentence you could re-read and catch — a wrong
                <b> action</b>{d.failure.irreversible ? ', and this one does not come back' : ''}.
              </p>
            : d.gatedAt
              ? <Value of={refused('the harness has not run it — a human is between the proposal and the action')} />
              : null}
        </div>
      )}

      <p className="g-body tol-punch"><Value of={cited(THE_SHIFT, SOURCE_4)} /></p>
      <p className="g-body g-dim"><Value of={cited(THE_GAP, SOURCE_3)} /> <Value of={cited(IN_A_LOOP, SOURCE_3)} /></p>

      <details className="cou-tr">
        <summary className="g-label">Which tier should a tool be in?</summary>
        <dl className="cou-dl">
          {TIERS.map((t) => <div key={t.id}><dt>{t.name}</dt><dd>{t.quote}</dd></div>)}
        </dl>
        <p className="g-body g-dim"><Value of={cited(CLAUDE_CODE_NOTE, SOURCE_5)} /></p>
      </details>

      <p className="g-body g-dim lab-why">
        Nothing here executes. The commands on screen are real ones and this page runs none of them,
        spawns nothing, and imports nothing that could — a lab about the danger of handing an AI a
        shell, which handed a browser a shell, is the joke that writes itself.
        <br />
        Notice too that approve-before does not make the AI propose anything better. It stops the
        proposal reaching the harness. Supervision puts a person between a call and an action; it
        does not improve the call, and a lab where a stricter setting produced a wiser one would be
        teaching that approval fixes judgement.
      </p>
    </div>
  );
}
